// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { authApi } from '../services/api';
import { jwtDecode } from 'jwt-decode';
import {
  isPasswordResetRequired,
  PASSWORD_RESET_REQUIRED_STORAGE_KEY,
} from '../utils/authRequest';
import { clearCommerceSessionStorage } from '../utils/commerceSessionStorage';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const isJwtToken = (token) => typeof token === 'string' && token.split('.').length === 3;

const isExpiredJwtToken = (token) => {
  if (!isJwtToken(token)) {
    return false;
  }

  try {
    const decodedToken = jwtDecode(token);
    if (!decodedToken?.exp) {
      return false;
    }

    return decodedToken.exp * 1000 < Date.now();
  } catch (error) {
    return true;
  }
};

const AuthProvider = ({ children }) => {
  const [authToken, setAuthToken] = useState(null);
  const [user, setUser] = useState(null);
  const [channelId, setChannel] = useState(null);
  const [branchId, setBranch] = useState(null);
  const [commerceName, setCommerceName] = useState(null);
  const [commerceLogo, setCommerceLogo] = useState(null);
  const [branchName, setBranchName] = useState(null);
  const [config, setConfig] = useState(null);
  const [passwordResetRequired, setPasswordResetRequired] = useState(false);
  const [loading, setLoading] = useState(true);
  const logoutInProgress = useRef(false);
  const acceptSessionUpdates = useRef(true);
  const sessionEpochRef = useRef(0);
  const [sessionEpoch, setSessionEpoch] = useState(0);

  const bumpSessionEpoch = () => {
    sessionEpochRef.current += 1;
    setSessionEpoch(sessionEpochRef.current);
    return sessionEpochRef.current;
  };

  const clearSession = () => {
    setAuthToken(null);
    setUser(null);
    setChannel(null);
    setBranch(null);
    setCommerceName(null);
    setCommerceLogo(null);
    setBranchName(null);
    setConfig(null);
    setPasswordResetRequired(false);
    clearCommerceSessionStorage();
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('user');
    const storedBranch = localStorage.getItem('branchId');
    const storedChannel = localStorage.getItem('channelId');
    const storedCommerce = localStorage.getItem('commerceName');
    const storedLogo = localStorage.getItem('commerceLogo');
    const storedBranchName = localStorage.getItem('branchName');
    const storedConfig = localStorage.getItem('config');

    if (token && token !== 'undefined') {
      try {
        if (isExpiredJwtToken(token)) {
          clearSession();
        } else {
          setAuthToken(token);
          setUser(storedUser ? JSON.parse(storedUser) : null);
          setBranch(storedBranch);
          setChannel(storedChannel);
          setCommerceName(storedCommerce);
          setCommerceLogo(storedLogo);
          setBranchName(storedBranchName);
          setConfig(storedConfig ? JSON.parse(storedConfig) : null);
          setPasswordResetRequired(localStorage.getItem(PASSWORD_RESET_REQUIRED_STORAGE_KEY) === '1');
        }
      } catch (error) {
        console.error('Invalid stored auth session:', error);
        clearSession();
      }
    }
    setLoading(false);
  }, []);

  const login = (token, userData, channelId, branchId, commerceName, branchName, commerceLogo, config, requiresPasswordReset = false) => {
    acceptSessionUpdates.current = true;
    bumpSessionEpoch();
    const mustChangePassword = isPasswordResetRequired(requiresPasswordReset);
    setAuthToken(token);
    setUser(userData);
    setChannel(channelId);
    setBranch(branchId);
    setCommerceName(commerceName);
    setCommerceLogo(commerceLogo);
    setBranchName(branchName);
    setConfig(config);
    setPasswordResetRequired(mustChangePassword);
    localStorage.setItem('authToken', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('branchId', branchId);
    localStorage.setItem('channelId', channelId);
    localStorage.setItem('commerceName', commerceName);
    localStorage.setItem('commerceLogo', commerceLogo);
    localStorage.setItem('branchName', branchName);
    localStorage.setItem('config', JSON.stringify(config));
    if (mustChangePassword) {
      localStorage.setItem(PASSWORD_RESET_REQUIRED_STORAGE_KEY, '1');
    } else {
      localStorage.removeItem(PASSWORD_RESET_REQUIRED_STORAGE_KEY);
    }
  };

  const completePasswordChange = (token, confirmation = {}) => {
    if (!acceptSessionUpdates.current) {
      return;
    }

    if (confirmation.sessionEpoch !== undefined && confirmation.sessionEpoch !== sessionEpochRef.current) {
      return;
    }

    if (confirmation.passwordResetRequired !== false) {
      return;
    }

    if (typeof token === 'string' && token !== '' && token !== 'undefined') {
      setAuthToken(token);
      localStorage.setItem('authToken', token);
    }
    setPasswordResetRequired(false);
    localStorage.removeItem(PASSWORD_RESET_REQUIRED_STORAGE_KEY);
  };

  const logout = async () => {
    if (logoutInProgress.current) {
      return;
    }

    logoutInProgress.current = true;
    acceptSessionUpdates.current = false;
    const logoutEpoch = bumpSessionEpoch();
    const token = authToken || localStorage.getItem('authToken');

    try {
      if (token && token !== 'undefined') {
        await authApi.post('/auth/logout', { strategy: 'comers' }, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      if (sessionEpochRef.current === logoutEpoch) {
        clearSession();
      }
      logoutInProgress.current = false;
    }
  };  

  return (
    <AuthContext.Provider
      value={{
        authToken,
        user,
        channelId,
        branchId,
        commerceName,
        branchName,
        commerceLogo,
        config,
        setConfig,
        passwordResetRequired,
        sessionEpoch,
        login,
        completePasswordChange,
        logout,
        loading
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
